import React, { HTMLAttributes } from "react";

// Utils
import { useClass } from "@breezerr";
import { usePage } from "@brz-utils/contexts/page.context";
import { TinaMarkdown } from 'tinacms/dist/rich-text';
import { filterTinaMarkdownContentParagraphs } from "@brz-utils/tina/filter.paragraphs";

// Components
import { Uppercase } from "@brz-components/elements/uppercase";
import { Button, Paper } from "@mui/material";
import { Img } from "@brz-components/elements/img";
import { TextLink } from "@brz-components/elements/text.link";
import { TextRotator } from "@brz-components/elements/text.rotator";
import { SectionHeroGenericDataModel, SectionHeroProposalDataModel } from "./types";
import { FloatingComponent } from "./floating.components";
import { RichCompanyName } from "@brz-components/elements/rich.company.name";
import { RichCurrentYear } from "@brz-components/elements/rich.current.year";
import { RichClientName } from "@brz-components/elements/rich.client.name";





const SectionHeroProposal = ({ data }: { data: SectionHeroProposalDataModel }) => {
    const { page } = usePage();
    const headerHeight = page._computed.topHeight(40);

    const [ready, setReady] = React.useState(false);
    
    React.useEffect(() => {
        if ( typeof window !== 'undefined' ) {
            setReady(true);
        }
    }, []);
    

    return <section className={useClass(`p-relative w-100% bg-color-grey:50 pt-${headerHeight / 10}rem ${data.floatingItems?.length > 1 ? 'mb-15rem' : 'mb-8rem'} md:mb-12rem d-flex flex-column lg:d-block`)}>
        <div className={useClass(`w-100% of-hidden p-relative z-index-0`)}>
            <div className={useClass(`container lg:d-grid grid-cols-12 gap-x-2x min-h-100%`)}>

                {/* Content */}
                <div className={useClass(`col-span-6 pt-4rem md:pt-6rem pb-4rem md:pb-8rem p-relative z-index-10 d-flex flex-column`)}>
                    <Uppercase className={useClass('d-none! lg:d-block! mb-3rem md:text-center lg:text-left')} component={'h1'}>{data.title}</Uppercase>
                    <h2 className={`${data.headingSize || 'h1'} ${useClass(`mb-3rem md:text-center lg:text-left d-block__br!`)}`}>
                        <TinaMarkdown content={filterTinaMarkdownContentParagraphs(data.headingRich, 'break')} components={{
                            TextRotator: (props: any) => {
                                return <span className={useClass('d-block')}>
                                    <TextRotator items={props.items} initialHeight={86} ContainerProps={{
                                        className: useClass('p-relative d-inline-flex bg-color-grey:300 radius-1rem pb-1rem px-2rem -ml-2rem sm:ml-0x lg:-ml-2rem lg:-bottom-0.5rem h-6.8rem md:h-8rem border-box')
                                    }} />
                                </span>
                            },
                            Break: (props: any) => {
                                return <>{new Array(props.size).fill(null).map((_, index) => (<br key={index} />))}</>
                            },
                            CompanyName: RichCompanyName,
                            ClientName: RichClientName,
                            CurrentYear: RichCurrentYear,
                        }} />
                    </h2>



                    {data.subheadingRich?.children.length && filterTinaMarkdownContentParagraphs(data.subheadingRich) ? (
                        <h3 className={useClass('text-400 text-size-2.1rem ls-0x mb-3rem lh-3rem')}>
                            <TinaMarkdown 
                                content={filterTinaMarkdownContentParagraphs(data.subheadingRich)} 
                                components={{
                                    highlight: (props: any) => {
                                        const color = props.color === 'accent' ? 'accent:100' : 'secondary:300';
                                        return <span className={useClass(`d-inline-block bg-color-${color} px-1rem my-0.1rem radius-1rem`)}>{ props.body }</span>
                                    }
                                }}
                            />
                        </h3>
                    ) : null}


                    
                    {data.textRich?.children.length && filterTinaMarkdownContentParagraphs(data.textRich) ? (
                        <div className={`multiline-highlight ${useClass('lg:max-w-42rem md:text-center lg:text-left text-size-1.6rem lh-2.5rem')}`}>
                            <TinaMarkdown 
                                content={data.textRich}
                                components={{
                                    highlight: (props: any) => {
                                        const color = props.color === 'accent' ? 'accent:100' : 'secondary:300';
                                        return <span className={useClass(`d-inline-block bg-color-${color} px-1rem py-0.5rem my-0.1rem radius-1rem`)}>{ props.body }</span>
                                    }
                                }}
                            />
                        </div>
                    ) : null}
                    
                    

                    {data.lists ? (
                        <div className={useClass('w-100% md:d-flex mt-3rem')}>{data.lists?.map((list, l) => {
                            return (
                                <div key={l} className={useClass(`${l > 0 ? 'md:ml-6rem' : ''} mb-3rem md:mb-0x`)}>
                                    <strong className={useClass('d-block mb-1rem')}>{ list.title }</strong>
                                    {list.items?.map((item, i) => {
                                        return <div key={i} className={useClass('d-flex align-items-center mb-0.5rem')}>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="5.838" height="8.847" viewBox="0 0 5.838 8.847">
                                                <path d="M0,0,3.01,3.01,6.019,0" transform="translate(1.414 7.433) rotate(-90)" fill="none" stroke="#54687a" strokeLinecap="round" strokeWidth="2" />
                                            </svg>
                                            <span className={useClass('ml-1rem')}>{ item }</span>
                                        </div>
                                    })}
                                </div>
                            )
                        })}</div>
                    ) : null}



                    {data.logos ? <>
                        <div className={useClass('d-flex flex-column h-100%')}>

                            <div className={useClass('d-block md:d-none w-100% h-1px bg-color-grey:400 mt-2rem mb-2rem')}></div>

                            <div className={useClass('mt-2rem md:mt-6rem')}>
                                {data.logos.highlightImgSrc ? (
                                    <Img className={useClass('mb-0.5rem')} src={data.logos.highlightImgSrc} />
                                ) : null}
                                {data.logos.highlightText ? (
                                    <div className={useClass('text-size-1.6rem lh-2.2rem')} dangerouslySetInnerHTML={{ __html: data.logos.highlightText }} />
                                ) : null}
                            </div>

                            <div className={useClass('d-block md:d-none w-100% h-1px bg-color-grey:400 mt-4rem')}></div>

                            <div className={useClass('mt-5rem md:mt-auto')}>
                                {data.logos.logosText ? (
                                    <div className={useClass('text-size-1.1rem ls-0.1rem uppercase mb-1rem')}>{ data.logos.logosText }</div>
                                ) : null}

                                {data.logos.logosList ? (
                                    <div className={useClass('d-flex flex-nowrap of-x-auto pb-2rem')}>{
                                        data.logos.logosList.map((item, index) => {
                                            return <Img key={index} className={useClass('h-3rem w-auto! mr-2rem')} src={item} />
                                        })
                                    }</div>
                                ) : null}
                            </div>
                        </div>
                    </> : null}


                    
                    
                    
                    {data.buttons ? (
                        <div className={useClass('d-flex flex-column lg:flex-row mb-2rem lg:mb-0x mt-4.5rem md:justify-content-center lg:justify-content-start')}>{
                            data.buttons?.map((button, index) => {
                                return index === 0 ? (
                                    <Button 
                                        key={index}
                                        size="large"
                                        variant="contained"
                                        color="secondary"
                                        href={button.link}
                                        component={TextLink}
                                    >{ button.label }</Button>
                                ) : (
                                    <Button 
                                        key={index}
                                        className={useClass('ml-0x md:ml-2rem mt-2rem md:mt-0x')}
                                        size="large"
                                        variant="outlined"
                                        color="primary"
                                        href={button.link}
                                        component={TextLink}
                                    >{ button.label }</Button>
                                )
                            })
                        }</div>
                    ) : null}
                </div>

                {/* Image */}
                <div className={useClass('p-relative col-span-6 md:min-h-100% d-flex md:align-items-end justify-content-center')}>
                    {data.image?.src ? (
                        <div className={useClass(`d-flex justify-content-center md:justify-content-end md:min-h-${730 * (typeof data.image.scale === 'number' ? data.image.scale : 1) / 10}rem`)}>
                            <Img
                                className={useClass('d-none lg:d-block p-relative md:left-0x md:-translate-x-50% md:translate-x-0x')}
                                src={data.image.src}
                                // width={data.image.aspectRatio ? 730 * data.image.aspectRatio : undefined}
                                height={730 * (typeof data.image.scale === 'number' ? data.image.scale : 1)}
                                loading={"eager"}
                                mode={"fit"}
                                quality={95}
                                aspectRatio={data.image.aspectRatio || undefined}
                            />

                            <Img
                                className={useClass('d-none md:d-block lg:d-none p-relative md:left-0x max-w-100% h-auto')}
                                src={data.image.src}
                                // width={600}
                                height={500}
                                loading={"lazy"}
                                mode={"fit"}
                                quality={95}
                                aspectRatio={data.image.aspectRatio || undefined}
                            />

                            <Img
                                className={useClass('d-block md:d-none p-relative md:left-0x md:-translate-x-50% md:translate-x-0x max-w-120% md:max-w-unset h-auto')}
                                src={data.image.src}
                                width={600}
                                // height={730}
                                loading={"eager"}
                                mode={"fit"}
                                quality={95}
                                aspectRatio={data.image.aspectRatio || undefined}
                            />
                        </div>
                    ) : null}
                </div>
            </div>
        </div>


        
        

        {/* 
          * Floating Items (Desktop)
          *
        */}
        <div className={useClass(`${data.floatingItems?.length === 1 ? '' : 'd-none lg:d-block'} p-absolute w-100% h-100% left-0x bottom-0x pointer-events-none z-index-10`)}>
            <div className={useClass('container lg:d-grid grid-cols-12 gap-x-3rem h-100%')}>
                <div className={useClass('col-start-6 col-end-13 p-relative h-100%')}>
                    {data.floatingItems?.length ? (
                        data.floatingItems.map((item, index) => {
                            let position = '';

                            switch (index) {
                                case 0:
                                    position = 'bottom-10% left-10%';
                                    break;
                                case 1:
                                    position = 'bottom-22% right-0x lg:-right-12%';
                                    break;
                                case 2:
                                    position = 'top-90% right-10%';
                                    break;
                            }

                            if ( data.floatingItems?.length === 1 ) {
                                position = '-bottom-2.5rem md:bottom-10% right-50% md:right-10% translate-x-50% md:translate-x-0%'
                            }


                            return (
                                <FloatingComponent 
                                    key={index}
                                    parentTypename={data.__typename} 
                                    data={item}
                                    ContainerProps={{
                                        className: useClass(`p-absolute pointer-events-auto ${position}`)
                                    }}
                                    forceDesktop={data.floatingItems?.length === 1}
                                />
                            );
                        })
                    ) : null}
                </div>
            </div>
        </div>

        {/* 
          * Floating Items (Mobile)
          *
        */}
        {data.floatingItems?.length > 1 ? (
            <div 
                className={`no-scrollbar scroll-x-snap ${useClass('p-absolute top-100% d-flex lg:d-none px-7.5vw pb-5rem border-box w-100% of-x-auto -translate-y-50%')}`}
                style={{ scrollPadding: '7.5vw' }}
            >
                {data.floatingItems?.length ? (
                    data.floatingItems.map((item, index) => {
                        let mx = 'mx-1rem';

                        if ( index === 0 ) { mx = 'mr-1rem'; }
                        if ( index === data.floatingItems.length - 1 ) { mx = 'ml-1rem'; }

                        return (
                            <FloatingComponent 
                                key={index}
                                parentTypename={data.__typename} 
                                data={item}
                                ContainerProps={{
                                    className: useClass(`${mx} d-flex`)
                                }}
                            />
                        );
                    })
                ) : null}
            </div>
        ) : null}
    </section>
};

export default SectionHeroProposal;